import * as React from 'react'
import { FC, useEffect, useState } from 'react'
import { Skeleton } from 'antd'
import { useDataProvider, useRecordContext } from 'react-admin'
import Button from '@mui/material/Button'

import { AccountScoped } from './AccountScoped'
import { Channel, Form, Notification } from 'shared/types'
import { ChannelSelector } from 'shared/organisms/ChannelSelector'
import AppState from 'shared/app_state'
import { TwoColumnInput } from 'shared/molecules/TwoColumnInput'

type Event = { label: string; type: string; description?: string }

type NotificationEditorProps = {
  initialEvent?: Event
  notifiableType?: string
  record?: Form
}

type NotificationEditorBaseProps = NotificationEditorProps & { accountId: string }

export const NotificationEditorBase: FC<NotificationEditorBaseProps> = ({
  accountId,
  initialEvent = {
    label: 'On New Entry',
    description: 'Triggers when a new entry is created.',
    type: 'Formtopia::Entries::OnCreate',
  },
  notifiableType = 'Formtopia::Form', // TODO make dynamic. hardcoded for now
}) => {
  const dataProvider = useDataProvider()
  const [channels, setChannels] = useState<Channel[]>(null)
  const [event, setEvent] = useState<Event>(initialEvent)
  const [notifications, setNotifications] = useState<Notification[]>(null)
  const notifiable = useRecordContext()

  const isLoading = () => !notifiable || channels == null || notifications == null

  const fetchChannels = async () => {
    try {
      const { data }: { data: any } = await dataProvider.getList('Channel', {
        pagination: { page: 1, perPage: 100 }, // TODO change. can't rely on 100 count notification limit
        sort: { field: null, order: null },
        filter: { status: 'active', accountId },
      })
      setChannels(data as Channel[])
    } catch (e) {
      console.log(e)
    }
  }

  const fetchNotifications = async (event) => {
    try {
      const { data }: { data: any } = await dataProvider.getList('Notification', {
        pagination: { page: 1, perPage: 100 }, // TODO change. can't rely on 100 count notification limit
        sort: { field: null, order: null },
        filter: { notifiableId: notifiable.id, type: event.type },
      })
      setNotifications(data as Notification[])
    } catch (e) {
      console.log(e)
    }
  }

  const notificationFromChannel = (c: Channel, notifications: Notification[]): Notification => {
    // notifications should already be filtered by event type b/c of fetchNotifications effect
    return notifications.find((n) => n.channel.id == c.id && n.notifiable.id == notifiable.id)
  }

  const onDisableChannel = async (c: Channel, onSuccess: () => void, onFailure: () => void) => {
    // delete notification for notifiable, channel pair
    const notification = notificationFromChannel(c, notifications)

    try {
      await dataProvider.delete('Notification', { id: notification.id, previousData: notification })
      await fetchNotifications(event)
      onSuccess()
    } catch (e) {
      console.log('error disabling channel notification', e)
      onFailure()
    }
  }

  const onEnableChannel = async (c: Channel, onSuccess: () => void, onFailure: () => void) => {
    // create notification for notifiable, channel pair
    try {
      await dataProvider.create('Notification', {
        data: {
          type: event.type,
          channelId: c.id,
          notifiableId: notifiable.id,
          notifiableType: notifiableType,
        },
      })
      await fetchNotifications(event)
      onSuccess()
    } catch (e) {
      console.log('error enabling channel notification', e)
      onFailure()
    }
  }

  useEffect(() => {
    // fetch channels once on component mount
    fetchChannels()
  }, [])

  useEffect(() => {
    // fetch notifications on event change
    if (event != null) {
      fetchNotifications(event)
    }
  }, [event])

  return (
    <>
      {isLoading() ? (
        <Skeleton active />
      ) : (
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexFlow: 'row wrap',
          }}
        >
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexFlow: 'row wrap',
            }}
          >
            {/* <div style={{
            flex: '1 0',
          }}> */}
            {/* use event label for now since assuming one event type */}
            {/* <h3>{event.label}</h3> */}
            {/* </div> */}
            <div
              style={{
                flex: '1 0',
              }}
            >
              <TwoColumnInput title="New Form Entry Received" description="Entries will be sent to these channels.">
                <ChannelSelector
                  channels={channels}
                  notificationFromChannel={notificationFromChannel}
                  notifications={notifications}
                  onDeselect={onDisableChannel}
                  onSelect={onEnableChannel}
                />
                <div>
                  <AccountScoped>
                    {(accountId: string) => (
                      <div style={{ textAlign: 'center', marginTop: 15 }}>
                        <Button
                          color="primary"
                          href={`#/account/${accountId}/channel/create`}
                          size="small"
                          style={{ marginRight: '15%' }}
                          // variant="contained"
                          onClick={() => AppState.setLastPage(window.location.pathname)}
                        >
                          + Add Another Channel
                        </Button>
                      </div>
                    )}
                  </AccountScoped>
                </div>
              </TwoColumnInput>
            </div>
          </div>
          <div
            style={{
              flex: '1 0',
              marginTop: 15,
            }}
          >
            {/* <TwoColumnInput title="New Form is Created" description="Entries will be sent to these channels.">
              <ChannelSelector
                channels={channels}
                notificationFromChannel={notificationFromChannel}
                notifications={notifications}
                onDeselect={onDisableChannel}
                onSelect={onEnableChannel}
              />
              <div>
                <AccountScoped>{
                  (accountId: string) => (
                    <div style={{ textAlign: 'center', marginTop: 15 }}>
                      <Button
                        color="primary"
                        href={`#/account/${accountId}/channel/create`}
                        size="small"
                        style={{ marginRight: '15%' }}
                        // variant="contained"
                        onClick={() => AppState.setLastPage(window.location.pathname)}
                      >
                        + Add Another Channel
                      </Button>
                    </div>
                  )
                }</AccountScoped>
              </div>
            </TwoColumnInput> */}
          </div>
        </div>
      )}
    </>
  )
}

export const NotificationEditor: FC<NotificationEditorProps> = (props) => {
  return <AccountScoped>{(accountId) => <NotificationEditorBase {...props} accountId={accountId} />}</AccountScoped>
}
